import { useState } from "react";
import { Link } from "react-router-dom";
import CutPrepForm from "@/components/CutPrepForm";
import TemplatesTab from "@/components/studio/TemplatesTab";
import CustomizeTab from "@/components/studio/CustomizeTab";
import FiltersTab from "@/components/studio/FiltersTab";
import ManotaTab from "@/components/studio/ManotaTab";
import FitTemplateTab from "@/components/studio/FitTemplateTab";
import FitSizeTab from "@/components/studio/FitSizeTab";
import SaveToManualButton from "@/components/studio/SaveToManualButton";
import StudioVideos from "@/components/studio/StudioVideos";
import { usePersistedState } from "@/hooks/usePersistedState";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { HelpHint } from "@/components/HelpHint";
import { Button } from "@/components/ui/button";
import { Upload, CheckCircle2, BookOpen } from "lucide-react";

export default function ProductionFile() {
  const [tab, setTab] = usePersistedState("pns_studio_tab", "fitsize");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const onPick = (f) => { if (!f) return; setFile(f); setPreview(URL.createObjectURL(f)); };

  const TAB_LABELS = { prep: "Cut & Bleed", templates: "Templates", customize: "Customize", filters: "Filters", ai: "AI Studio", fit: "Fit to Template", fitsize: "Fit to Size" };
  const content = (value, node) => (
    <TabsContent value={value} forceMount className="pt-4 data-[state=inactive]:hidden">
      <div className="flex justify-end gap-2 mb-2">
        <StudioVideos refId={value} refLabel={TAB_LABELS[value] || "Print Studio"} label="Videos" />
        <SaveToManualButton tab={value} />
      </div>
      {node}
    </TabsContent>
  );

  return (
    <div className="p-8 max-w-6xl" data-testid="print-studio-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-slate-400">Print Studio</p>
          <h1 className="font-head text-3xl font-black mb-1 flex items-center gap-2">
            Print Studio
            <HelpHint side="right">Your hub to turn client files into print-ready output: prep cut &amp; bleed, keep a library of your production templates, and drop client artwork straight into them.</HelpHint>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <StudioVideos label="Guides" refLabel="Print Studio" />
          <Link to="/print-studio-manual">
            <Button variant="outline" size="sm" data-testid="studio-manual-link"><BookOpen size={15} className="mr-1.5" /> User Manual</Button>
          </Link>
        </div>
      </div>
      <p className="text-slate-500 text-sm mb-6">Prep artwork, manage your template library, and fit client files into your production templates.</p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="studio-tabs">
          <TabsTrigger value="prep" data-testid="studio-tab-prep">Cut &amp; Bleed</TabsTrigger>
          <TabsTrigger value="templates" data-testid="studio-tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="customize" data-testid="studio-tab-customize">Customize</TabsTrigger>
          <TabsTrigger value="filters" data-testid="studio-tab-filters">Filters</TabsTrigger>
          <TabsTrigger value="ai" data-testid="studio-tab-ai">AI Studio</TabsTrigger>
          <TabsTrigger value="fit" data-testid="studio-tab-fit">Fit to Template</TabsTrigger>
          <TabsTrigger value="fitsize" data-testid="studio-tab-fitsize">Fit to Size</TabsTrigger>
        </TabsList>

        {content("prep", (
          <>
            <p className="text-slate-500 text-sm mb-4 flex items-center gap-2">
              Turn a client image into a VersaWorks-ready PDF with bleed and a <span className="font-semibold text-[#2495D3]">CutContour</span> line.
              <HelpHint side="right">Upload one client image (PNG/JPG/PDF), pick a cut mode and final size; the system adds bleed and the cut line. For placing a ready client file into your own template, use the <b>Fit to Template</b> tab.</HelpHint>
            </p>
            <label className="flex flex-col items-center justify-center gap-2 h-36 rounded-xl border-2 border-dashed border-slate-300 cursor-pointer hover:border-[#2495D3] text-sm text-slate-500 bg-slate-50/50 mb-6" data-testid="prod-drop">
              {file ? <><CheckCircle2 size={22} className="text-emerald-500" /><span className="max-w-[240px] truncate">{file.name}</span><span className="text-[11px] text-slate-400">Click to replace</span></>
                    : <><Upload size={24} /><span>Upload artwork (PNG / JPG / PDF / screenshot)</span><span className="text-[11px] text-slate-400">PNG with transparent background traces cleanest</span></>}
              <input data-testid="prod-file" type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden" onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            {file ? <CutPrepForm file={file} previewUrl={preview} initW="2" initH="2" initMode="shape" />
                  : <p className="text-sm text-slate-400">Upload artwork to configure the cut &amp; bleed.</p>}
          </>
        ))}

        {content("templates", <TemplatesTab />)}
        {content("customize", <CustomizeTab />)}
        {content("filters", <FiltersTab />)}
        {content("ai", <ManotaTab />)}
        {content("fit", <FitTemplateTab />)}
        {content("fitsize", <FitSizeTab />)}
      </Tabs>
    </div>
  );
}
